import React, { useState, useEffect, useContext } from 'react';
import {
  Snackbar,
  Alert,
  Box,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip
} from '@mui/material';
import {
  Notifications,
  Sync,
  CheckCircle,
  Warning,
  Error,
  Receipt,
  AccountBalance,
  Close,
  MarkAsUnread,
  Delete,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import quickbooksService from '../../services/quickbooksService';

// Utility function for notification icons
const getNotificationIcon = (notification) => {
  return notification.icon || <Notifications />;
};

// Custom hook for QuickBooks notifications
export const useQuickBooksNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev.slice(0, 19)]); // Keep latest 20
    setUnreadCount(prev => prev + 1);

    // Auto-remove success notifications after 5 seconds
    if (notification.type === 'success') {
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, 5000);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => {
      const notification = prev.find(n => n.id === id);
      const newNotifications = prev.filter(n => n.id !== id);

      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return newNotifications;
    });
  };

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(n => {
        if (n.id === id && !n.read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
          return { ...n, read: true };
        }
        return n;
      })
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  // Notification types
  const notifySync = (result) => {
    if (result.success) {
      addNotification({
        type: 'success',
        title: 'Sync Complete',
        message: result.message || 'QuickBooks transactions synced successfully',
        icon: <Sync />,
        data: result.stats
      });
    } else {
      addNotification({
        type: 'error',
        title: 'Sync Failed',
        message: result.error || 'Failed to sync QuickBooks transactions',
        icon: <Error />
      });
    }
  };

  const notifyMatch = (transactionCount) => {
    addNotification({
      type: 'success',
      title: 'Transactions Matched',
      message: `Successfully matched ${transactionCount} transaction${transactionCount !== 1 ? 's' : ''} with receipts`,
      icon: <CheckCircle />
    });
  };

  const notifyConnection = (status, companyName) => {
    if (status === 'connected') {
      addNotification({
        type: 'success',
        title: 'QuickBooks Connected',
        message: `Successfully connected to ${companyName}`,
        icon: <AccountBalance />
      });
    } else if (status === 'disconnected') {
      addNotification({
        type: 'warning',
        title: 'QuickBooks Disconnected',
        message: 'QuickBooks account has been disconnected',
        icon: <Warning />
      });
    } else if (status === 'error') {
      addNotification({
        type: 'error',
        title: 'Connection Error',
        message: 'QuickBooks connection failed. Please check your authorization.',
        icon: <Error />
      });
    }
  };

  const notifyReceiptUpload = (receiptName, success = true) => {
    addNotification({
      type: success ? 'success' : 'error',
      title: success ? 'Receipt Uploaded' : 'Upload Failed',
      message: success
        ? `Receipt "${receiptName}" uploaded to QuickBooks successfully`
        : `Failed to upload receipt "${receiptName}" to QuickBooks`,
      icon: <Receipt />
    });
  };

  const notifyWebhook = (eventCount, entityTypes) => {
    addNotification({
      type: 'info',
      title: 'Data Updated',
      message: `${eventCount} QuickBooks ${entityTypes.join(', ')} record(s) updated`,
      icon: <Refresh />
    });
  };

  const notifyAutoMatch = (matchCount, confidence) => {
    addNotification({
      type: 'info',
      title: 'Auto-Match Complete',
      message: `${matchCount} transaction(s) automatically matched with ${confidence}% average confidence`,
      icon: <CheckCircle />
    });
  };

  return {
    notifications,
    unreadCount,
    addNotification,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
    // Helper methods
    notifySync,
    notifyMatch,
    notifyConnection,
    notifyReceiptUpload,
    notifyWebhook,
    notifyAutoMatch
  };
};

// Notification Bell Component
export const QuickBooksNotificationBell = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    removeNotification
  } = useQuickBooksNotifications();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate to relevant page based on notification type
    if (notification.title.includes('Sync')) {
      window.location.href = '/quickbooks/dashboard';
    } else if (notification.title.includes('Match')) {
      window.location.href = '/quickbooks/reconciliation';
    }

    handleClose();
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ ml: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Notifications />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 360, maxHeight: 480 }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box px={2} py={1} display="flex" justifyContent="between" alignItems="center">
          <Typography variant="h6">QuickBooks</Typography>
          <Box>
            {notifications.length > 0 && (
              <>
                <Button size="small" onClick={markAllAsRead}>
                  Mark All Read
                </Button>
                <Button size="small" onClick={clearAll} color="error">
                  Clear All
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Divider />

        {notifications.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Notifications sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                borderLeft: notification.read ? 'none' : `4px solid`,
                borderLeftColor: `${getNotificationColor(notification.type)}.main`,
                bgcolor: notification.read ? 'inherit' : 'action.hover',
                '&:hover': {
                  bgcolor: notification.read ? 'action.hover' : 'action.selected'
                }
              }}
            >
              <ListItemIcon>
                {getNotificationIcon(notification)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="between" alignItems="flex-start">
                    <Typography variant="subtitle2" sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}>
                      {notification.title}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNotification(notification.id);
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                        mb: 0.5
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Box display="flex" justifyContent="between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(notification.timestamp)}
                      </Typography>
                      {!notification.read && (
                        <Chip
                          label="New"
                          size="small"
                          color="primary"
                          sx={{ height: 16, fontSize: '0.625rem' }}
                        />
                      )}
                    </Box>
                    {notification.data && (
                      <Box mt={0.5}>
                        {notification.data.totalSynced && (
                          <Typography variant="caption" color="text.secondary">
                            {notification.data.totalSynced} transactions • {notification.data.totalNew} new
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

// Toast Notification Component
export const QuickBooksToastNotifications = () => {
  const { notifications, removeNotification } = useQuickBooksNotifications();
  const [currentNotification, setCurrentNotification] = useState(null);

  useEffect(() => {
    // Show the latest unread notification as toast
    const latestUnread = notifications.find(n => !n.read && !n.showedAsToast);
    if (latestUnread) {
      setCurrentNotification(latestUnread);
      // Mark as showed as toast to avoid showing again
      latestUnread.showedAsToast = true;
    }
  }, [notifications]);

  const handleClose = () => {
    setCurrentNotification(null);
  };

  if (!currentNotification) return null;

  return (
    <Snackbar
      open={true}
      autoHideDuration={currentNotification.type === 'success' ? 4000 : 6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        severity={currentNotification.type}
        onClose={handleClose}
        variant="filled"
        sx={{ minWidth: 300 }}
        icon={getNotificationIcon(currentNotification)}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          {currentNotification.title}
        </Typography>
        <Typography variant="body2">
          {currentNotification.message}
        </Typography>
      </Alert>
    </Snackbar>
  );
};

// Context provider for notifications
export const QuickBooksNotificationProvider = ({ children }) => {
  const notificationHooks = useQuickBooksNotifications();

  // Set up WebSocket or polling for real-time updates
  useEffect(() => {
    // This would connect to your WebSocket or set up polling
    // For now, we'll simulate with a simple interval check

    const checkForUpdates = async () => {
      try {
        // This could be replaced with WebSocket events
        const config = await quickbooksService.getConfig();
        if (config.connected && config.lastSync) {
          const lastCheck = localStorage.getItem('qb_last_notification_check');
          const lastSyncTime = new Date(config.lastSync).getTime();

          if (!lastCheck || lastSyncTime > parseInt(lastCheck)) {
            // New sync detected
            localStorage.setItem('qb_last_notification_check', lastSyncTime.toString());
          }
        }
      } catch (error) {
        // Silently handle errors in background checks
      }
    };

    const interval = setInterval(checkForUpdates, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <QuickBooksNotificationContext.Provider value={notificationHooks}>
      {children}
      <QuickBooksToastNotifications />
    </QuickBooksNotificationContext.Provider>
  );
};

// Create context
export const QuickBooksNotificationContext = React.createContext(null);

// Hook to use notifications
export const useQuickBooksNotificationContext = () => {
  const context = useContext(QuickBooksNotificationContext);
  if (!context) {
    throw new Error('useQuickBooksNotificationContext must be used within QuickBooksNotificationProvider');
  }
  return context;
};

export default QuickBooksNotificationBell;